// Phase 21.6B — Event form helpers.
// Timezone handling is explicit (no browser implicit conversion). Presets
// are client-side only — no template_id is ever persisted to the backend.

export const TIMEZONES = [
  'America/Los_Angeles',
  'America/Denver',
  'America/Phoenix',
  'America/Chicago',
  'America/New_York',
  'America/Anchorage',
  'Pacific/Honolulu',
  'UTC',
];

export const VISIBILITY_SCOPES = [
  { value: 'public', label: 'Public — Visitors + all authenticated users' },
  { value: 'all_portal_users', label: 'All portal users' },
  { value: 'agency', label: 'Agency (optionally downline)' },
  { value: 'specific_users', label: 'Specific users' },
  { value: 'kr_internal', label: 'KR internal only' },
];

// Phase 26.8 — friendlier facets on top of visibility_scope.
// Audience → visibility_scope mapping is 1:1 (mirrored in
// /app/backend/services/event_facets.py).
export const AUDIENCE_OPTIONS = [
  { value: 'all_agents',       scope: 'all_portal_users', label: 'All Agents' },
  { value: 'specific_agency',  scope: 'agency',           label: 'Specific Agency' },
  { value: 'kr_staff',         scope: 'kr_internal',      label: 'KR Staff' },
  { value: 'public',           scope: 'public',           label: 'Public' },
  { value: 'invitation_only',  scope: 'specific_users',   label: 'Invitation Only' },
];
export const audienceLabel = (v) =>
  AUDIENCE_OPTIONS.find((o) => o.value === v)?.label || v || '—';
export const audienceToScope = (a) =>
  AUDIENCE_OPTIONS.find((o) => o.value === a)?.scope || 'all_portal_users';
export const scopeToAudience = (s) =>
  AUDIENCE_OPTIONS.find((o) => o.scope === s)?.value || 'all_agents';
export const visibilityFromScope = (s) => (s === 'public' ? 'public' : 'internal');

export const OWNER_OPTIONS = [
  { value: 'kr',     label: 'KR (KR Enterprise Group)' },
  { value: 'agency', label: 'Agency' },
];

// Phase 26.1.1 — canonical Event Type + Business Category (backend-provided
// via /api/calendar/enums; labels curated here for UI display).
export const EVENT_TYPE_OPTIONS = [
  { value: 'training', label: 'Training' },
  { value: 'certification', label: 'Certification' },
  { value: 'webinar', label: 'Webinar' },
  { value: 'company_event', label: 'Company Event' },
  { value: 'agency_event', label: 'Agency Event' },
  { value: 'meeting', label: 'Meeting' },
  { value: 'office_event', label: 'Office Event' },
  { value: 'conference', label: 'Conference' },
  { value: 'informational', label: 'Informational — Non-registerable' },
  { value: 'other', label: 'Other' },
];

export const BUSINESS_CATEGORY_OPTIONS = [
  { value: 'medicare', label: 'Medicare' },
  { value: 'aca', label: 'ACA' },
  { value: 'life', label: 'Life' },
  { value: 'annuities', label: 'Annuities' },
  { value: 'supplemental', label: 'Supplemental' },
  { value: 'dental_vision', label: 'Dental & Vision' },
  { value: 'recruiting', label: 'Recruiting' },
  { value: 'compliance', label: 'Compliance' },
  { value: 'general', label: 'General' },
];

// Phase 26.1.2 — Language (optional, independent from title)
export const LANGUAGE_OPTIONS = [
  { value: 'english', label: 'English' },
  { value: 'spanish', label: 'Spanish' },
  { value: 'bilingual', label: 'Bilingual' },
  { value: 'other', label: 'Other' },
];

export const eventTypeLabel = (v) =>
  EVENT_TYPE_OPTIONS.find((o) => o.value === v)?.label || v || '—';
export const businessCategoryLabel = (v) =>
  BUSINESS_CATEGORY_OPTIONS.find((o) => o.value === v)?.label || v || '—';
export const languageLabel = (v) =>
  LANGUAGE_OPTIONS.find((o) => o.value === v)?.label || v || '—';

// Client-side only convenience prefills. Never sent to backend as template_id.
export const PRESETS = [
  {
    key: 'training',
    label: 'Training Session',
    values: {
      category: 'training',
      visibility_scope: 'all_portal_users',
      description: 'Agent training session covering product knowledge and platform usage.',
      requires_rsvp: true,
      capacity: 50,
    },
  },
  {
    key: 'carrier_webinar',
    label: 'Carrier Webinar',
    values: {
      category: 'carrier_webinar',
      visibility_scope: 'all_portal_users',
      description: 'Carrier-hosted product webinar.',
      requires_rsvp: false,
    },
  },
  {
    key: 'community_event',
    label: 'Community Event',
    values: {
      category: 'community_event',
      visibility_scope: 'public',
      description: 'Public community outreach event.',
      requires_rsvp: false,
    },
  },
  {
    key: 'internal_kr_meeting',
    label: 'KR Internal Meeting',
    values: {
      category: 'internal_kr_meeting',
      visibility_scope: 'kr_internal',
      description: 'Internal KR staff meeting.',
      requires_rsvp: true,
    },
  },
  {
    key: 'recruiting_event',
    label: 'Recruiting Event',
    values: {
      category: 'recruiting_event',
      visibility_scope: 'agency',
      description: 'Recruiting event for prospective agents.',
      requires_rsvp: true,
    },
  },
];

// ── Phase 27.x — Guided creation wizard: 7 canonical event types ───
// Each type controls the legacy `category`, sensible defaults, which
// drawer sections are relevant, and a few type-specific toggles. This is
// the single source of truth for the step-based creation flow. Icons are
// lucide-react component names, resolved in the wizard component.
export const EVENT_TYPE_WIZARD = [
  {
    key: 'community_marketing',
    label: 'Community / Marketing Event',
    useFor: 'Use this for festivals, fairs, booths and lead-generation activities.',
    icon: 'Megaphone',
    category: 'community_event',
    sections: ['general', 'schedule', 'audience', 'location', 'registration', 'attachments', 'notifications'],
    flags: { leadCapture: true, carrier: false, npn: false, recognitionLink: false, slots: true },
    defaults: { visibility_scope: 'all_portal_users', requires_rsvp: false, live_capture_enabled: true },
  },
  {
    key: 'training',
    label: 'Training',
    useFor: 'Use this for agent trainings — product knowledge and platform education.',
    icon: 'GraduationCap',
    category: 'training',
    sections: ['general', 'schedule', 'audience', 'location', 'registration', 'attachments', 'notifications'],
    flags: { leadCapture: false, carrier: true, npn: false, recognitionLink: false, slots: false },
    defaults: { visibility_scope: 'all_portal_users', requires_rsvp: true },
  },
  {
    key: 'carrier_event',
    label: 'Carrier Webinar / Carrier Event',
    useFor: 'Use this for carrier-hosted webinars and product events.',
    icon: 'Building2',
    category: 'carrier_webinar',
    sections: ['general', 'schedule', 'audience', 'location', 'registration', 'attachments', 'notifications'],
    flags: { leadCapture: false, carrier: true, npn: false, recognitionLink: false, slots: false },
    defaults: { visibility_scope: 'all_portal_users', requires_rsvp: false },
  },
  {
    key: 'kr_internal_meeting',
    label: 'KR Internal Meeting',
    useFor: 'Use this for internal KR staff meetings and syncs.',
    icon: 'Users',
    category: 'internal_kr_meeting',
    sections: ['general', 'schedule', 'audience', 'location', 'notifications'],
    flags: { leadCapture: false, carrier: false, npn: false, recognitionLink: false, slots: false },
    defaults: { visibility_scope: 'kr_internal', requires_rsvp: false },
  },
  {
    key: 'recruiting_event',
    label: 'Recruiting Event',
    useFor: 'Use this for recruiting drives — may appear publicly and capture prospects.',
    icon: 'UserPlus',
    category: 'recruiting_event',
    sections: ['general', 'schedule', 'audience', 'location', 'registration', 'attachments', 'notifications'],
    flags: { leadCapture: false, carrier: false, npn: true, recognitionLink: false, slots: false },
    defaults: { visibility_scope: 'public', requires_rsvp: true, requires_npn_to_register: false },
  },
  {
    key: 'certification_day',
    label: 'Certification Day',
    useFor: 'Use this for certification days with check-in and a defined audience.',
    icon: 'BadgeCheck',
    category: 'certification',
    sections: ['general', 'schedule', 'audience', 'location', 'registration', 'attachments', 'notifications'],
    flags: { leadCapture: false, carrier: true, npn: false, recognitionLink: false, slots: false },
    defaults: { visibility_scope: 'all_portal_users', requires_rsvp: true },
  },
  {
    key: 'recognition_event',
    label: 'Recognition Event',
    useFor: 'Use this for recognition celebrations; link a Recognition Program if applicable.',
    icon: 'Award',
    category: 'other',
    sections: ['general', 'schedule', 'audience', 'location', 'registration', 'attachments', 'notifications'],
    flags: { leadCapture: false, carrier: false, npn: false, recognitionLink: true, slots: false },
    defaults: { visibility_scope: 'all_portal_users', requires_rsvp: true },
  },
];

export const wizardTypeByKey = (k) => EVENT_TYPE_WIZARD.find((t) => t.key === k) || null;

// Map a legacy `category` back to a wizard type for edit mode. Returns null
// for ambiguous/legacy categories (e.g. 'other') so the drawer falls back to
// showing every section rather than guessing.
export const wizardTypeByCategory = (c) => {
  const alias = {
    carrier_event: 'carrier_event',
    carrier_webinar: 'carrier_event',
    meeting: 'kr_internal_meeting',
    internal_kr_meeting: 'kr_internal_meeting',
    grassroots_event: 'community_marketing',
    food_pantry: 'community_marketing',
    community_event: 'community_marketing',
    training: 'training',
    certification: 'certification_day',
    recruiting_event: 'recruiting_event',
    other: 'recognition_event',
  };
  return wizardTypeByKey(alias[c]) || null;
};

// Build the initial create-mode form from a chosen type + step-2 basics.
export function seedFromWizard(typeObj, basics = {}) {
  let form = { ...blankEvent(), category: typeObj.category, ...(typeObj.defaults || {}) };
  form = { ...form, ...basics };
  form = applyVisibilityScopeChange(form, form.visibility_scope);
  return form;
}

// ── Datetime helpers — explicit IANA timezone handling ────────────
// The browser <input type="datetime-local"> yields a naive string like
// "2026-03-01T14:00". We combine that with the caller-selected IANA
// timezone to produce a correct UTC ISO string, and vice versa.

export function localToUTCISO(localStr, tz) {
  if (!localStr || !tz) return null;
  // Interpret "YYYY-MM-DDTHH:MM" as if it were UTC to get an anchor.
  const anchor = new Date(`${localStr}:00Z`);
  if (Number.isNaN(anchor.getTime())) return null;
  // How does that same anchor render in the target tz?
  const fmt = new Intl.DateTimeFormat('en-US', {
    timeZone: tz,
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
    hour12: false,
  });
  const parts = Object.fromEntries(fmt.formatToParts(anchor).map((p) => [p.type, p.value]));
  const asIfInTz = Date.UTC(
    +parts.year, +parts.month - 1, +parts.day,
    parts.hour === '24' ? 0 : +parts.hour, +parts.minute, +parts.second,
  );
  const offsetMs = asIfInTz - anchor.getTime();
  return new Date(anchor.getTime() - offsetMs).toISOString();
}

export function utcISOToLocal(iso, tz) {
  if (!iso || !tz) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const fmt = new Intl.DateTimeFormat('en-CA', {
    timeZone: tz,
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit',
    hour12: false,
  });
  const parts = Object.fromEntries(fmt.formatToParts(d).map((p) => [p.type, p.value]));
  const hh = parts.hour === '24' ? '00' : parts.hour;
  return `${parts.year}-${parts.month}-${parts.day}T${hh}:${parts.minute}`;
}

// ── Form state helpers ────────────────────────────────────────────

export function blankEvent() {
  return {
    title: '',
    category: 'training',
    event_type: '',
    business_category: '',
    language: '',
    description: '',
    internal_notes: '',
    visibility_scope: 'all_portal_users',
    inherit_to_downline: false,
    live_capture_enabled: false,
    live_capture_agent_npns: [],
    track_sales: false,
    sales_agent_npns: [],
    // Phase 26.8 — Owner facet.
    owner_type: 'kr',
    owner_agency_id: '',
    start_local: '',
    end_local: '',
    timezone: 'America/Los_Angeles',
    all_day: false,
    physical_location: { street: '', city: '', state: '', zip: '', country: 'US', room: '', notes: '' },
    virtual_meeting_url: '',
    expose_meeting_url_publicly: false,
    contact_name: '',
    contact_phone: '',
    contact_email: '',
    carrier_id: '',
    audience_agency_id: '',
    audience_user_ids: [], // list of {user_id, label}
    capacity: '',
    requires_rsvp: false,
    // Phase 26.2 — Registration & RSVP configuration
    registration_deadline_local: '',
    allow_late_registration: false,
    registration_allow_guests: false,
    max_guests_per_registration: 0,
    // Phase 26.4 — Waitlist configuration
    waitlist_enabled: false,
    waitlist_accepting: true,
    // Phase 26.5 — Email notification settings
    email_notifications_enabled: true,
    send_registration_confirmation: true,
    send_waitlist_notifications: true,
    send_event_update_notifications: true,
    send_reminder_24h: true,
    send_reminder_1h: true,
    requires_npn_to_register: false,
    recognition_program_id: '',
  };
}

// When visibility_scope changes we MUST clear audience fields that are
// incompatible with the new scope. This runs on the form state; the
// payload builder also strips them defensively.
export function applyVisibilityScopeChange(form, newScope) {
  const next = { ...form, visibility_scope: newScope };
  if (newScope !== 'agency') {
    next.audience_agency_id = '';
    next.inherit_to_downline = false;
  }
  if (newScope !== 'specific_users') {
    next.audience_user_ids = [];
  }
  if (newScope !== 'public') {
    next.expose_meeting_url_publicly = false;
  }
  return next;
}

export function fromRecord(record) {
  if (!record) return blankEvent();
  return {
    ...blankEvent(),
    title: record.title || '',
    category: record.category || 'training',
    event_type: record.event_type || '',
    business_category: record.business_category || '',
    language: record.language || '',
    description: record.description || '',
    internal_notes: record.internal_notes || '',
    visibility_scope: record.visibility_scope || 'all_portal_users',
    inherit_to_downline: !!record.inherit_to_downline,
    live_capture_enabled: !!record.live_capture_enabled,
    live_capture_agent_npns: record.live_capture_agent_npns || [],
    track_sales: !!record.track_sales,
    sales_agent_npns: record.sales_agent_npns || [],
    // Phase 26.8 — Owner facet (defaults to 'kr' for legacy events).
    owner_type: record.owner_type || 'kr',
    owner_agency_id: record.owner_agency_id || '',
    start_local: utcISOToLocal(record.start_datetime, record.timezone || 'America/Los_Angeles'),
    end_local: utcISOToLocal(record.end_datetime, record.timezone || 'America/Los_Angeles'),
    timezone: record.timezone || 'America/Los_Angeles',
    all_day: !!record.all_day,
    physical_location: {
      street: record.physical_location?.street || '',
      city: record.physical_location?.city || '',
      state: record.physical_location?.state || '',
      zip: record.physical_location?.zip || '',
      country: record.physical_location?.country || 'US',
      room: record.physical_location?.room || '',
      notes: record.physical_location?.notes || '',
    },
    virtual_meeting_url: record.virtual_meeting_url || '',
    expose_meeting_url_publicly: !!record.expose_meeting_url_publicly,
    contact_name: record.contact_name || '',
    contact_phone: record.contact_phone || '',
    contact_email: record.contact_email || '',
    carrier_id: record.carrier_id || '',
    audience_agency_id: record.audience_agency_id || '',
    audience_user_ids: (record.audience_user_ids || []).map((id) => ({ user_id: id, label: id })),
    capacity: record.capacity ?? '',
    requires_rsvp: !!record.requires_rsvp,
    registration_deadline_local: utcISOToLocal(record.registration_deadline, record.timezone || 'America/Los_Angeles'),
    allow_late_registration: !!record.allow_late_registration,
    registration_allow_guests: !!record.registration_allow_guests,
    max_guests_per_registration: record.max_guests_per_registration ?? 0,
    waitlist_enabled: !!record.waitlist_enabled,
    waitlist_accepting: record.waitlist_accepting !== false,
    email_notifications_enabled: record.email_notifications_enabled !== false,
    send_registration_confirmation: record.send_registration_confirmation !== false,
    send_waitlist_notifications: record.send_waitlist_notifications !== false,
    send_event_update_notifications: record.send_event_update_notifications !== false,
    send_reminder_24h: record.send_reminder_24h !== false,
    send_reminder_1h: record.send_reminder_1h !== false,
    requires_npn_to_register: !!record.requires_npn_to_register,
    recognition_program_id: record.recognition_program_id || '',
  };
}

// Build the payload. `excludeImmutable=true` for PATCH on published events.
export function buildPayload(form, { excludeImmutable = false, isEdit = false } = {}) {
  const p = {
    title: form.title.trim(),
    category: form.category,
    event_type: form.event_type || null,
    business_category: form.business_category || null,
    language: form.language || null,
    description: form.description?.trim() || null,
    internal_notes: form.internal_notes?.trim() || null,
    visibility_scope: form.visibility_scope,
    inherit_to_downline: !!form.inherit_to_downline,
    live_capture_enabled: !!form.live_capture_enabled,
    live_capture_agent_npns: form.live_capture_enabled ? (form.live_capture_agent_npns || []) : [],
    track_sales: !!form.track_sales,
    sales_agent_npns: form.track_sales ? (form.sales_agent_npns || []) : [],
    // Phase 26.8 — Owner facet.
    owner_type: form.owner_type || 'kr',
    owner_agency_id: form.owner_type === 'agency' ? (form.owner_agency_id?.trim() || null) : null,
    start_datetime: localToUTCISO(form.start_local, form.timezone),
    end_datetime: localToUTCISO(form.end_local, form.timezone),
    timezone: form.timezone,
    all_day: !!form.all_day,
    virtual_meeting_url: form.virtual_meeting_url?.trim() || null,
    expose_meeting_url_publicly: !!form.expose_meeting_url_publicly,
    contact_name: form.contact_name?.trim() || null,
    contact_phone: form.contact_phone?.trim() || null,
    contact_email: form.contact_email?.trim() || null,
    carrier_id: form.carrier_id?.trim() || null,
    capacity: form.capacity === '' || form.capacity == null ? null : Number(form.capacity),
    requires_rsvp: !!form.requires_rsvp,
    registration_deadline: localToUTCISO(form.registration_deadline_local, form.timezone),
    allow_late_registration: !!form.allow_late_registration,
    registration_allow_guests: !!form.registration_allow_guests,
    max_guests_per_registration: Math.max(0, Math.min(20, Number(form.max_guests_per_registration) || 0)),
    waitlist_enabled: !!form.waitlist_enabled,
    waitlist_accepting: !!form.waitlist_accepting,
    email_notifications_enabled: !!form.email_notifications_enabled,
    send_registration_confirmation: !!form.send_registration_confirmation,
    send_waitlist_notifications: !!form.send_waitlist_notifications,
    send_event_update_notifications: !!form.send_event_update_notifications,
    send_reminder_24h: !!form.send_reminder_24h,
    send_reminder_1h: !!form.send_reminder_1h,
    requires_npn_to_register: !!form.requires_npn_to_register,
    recognition_program_id: form.recognition_program_id?.trim() || null,
  };
  // Physical location: only include if any field set.
  const loc = form.physical_location || {};
  const locNonEmpty = ['street', 'city', 'state', 'zip', 'room', 'notes'].some((k) => (loc[k] || '').trim() !== '');
  p.physical_location = locNonEmpty
    ? {
        street: loc.street?.trim() || null,
        city: loc.city?.trim() || null,
        state: loc.state?.trim() || null,
        zip: loc.zip?.trim() || null,
        country: loc.country?.trim() || 'US',
        room: loc.room?.trim() || null,
        notes: loc.notes?.trim() || null,
      }
    : null;

  // Defensive audience clearing based on scope.
  if (p.visibility_scope === 'agency') {
    p.audience_agency_id = form.audience_agency_id?.trim() || null;
    p.audience_user_ids = [];
  } else if (p.visibility_scope === 'specific_users') {
    p.audience_agency_id = null;
    p.inherit_to_downline = false;
    p.audience_user_ids = (form.audience_user_ids || [])
      .map((u) => (typeof u === 'string' ? u : u.user_id))
      .filter(Boolean);
  } else {
    p.audience_agency_id = null;
    p.audience_user_ids = [];
    if (p.visibility_scope !== 'agency') p.inherit_to_downline = false;
    if (p.visibility_scope !== 'public') p.expose_meeting_url_publicly = false;
  }

  // For PATCH: strip immutable-after-publish fields.
  if (excludeImmutable) {
    delete p.canonical_slug;
    delete p.created_by_user_id;
    delete p.published_at;
  }

  // For PATCH, EventUpdate has extra="forbid" but every field is Optional;
  // sending values is fine. For POST (EventCreate), the same shape works.
  // Note: EventCreate & EventUpdate both forbid unknown keys — we only
  // send fields the model defines.
  void isEdit;
  return p;
}

export function validateForm(form) {
  const errors = {};
  if (!form.title.trim()) errors.title = 'Title is required';
  if (!form.start_local) errors.start_local = 'Start is required';
  if (!form.end_local) errors.end_local = 'End is required';
  if (form.start_local && form.end_local && form.end_local < form.start_local) {
    errors.end_local = 'End must be at or after start';
  }
  if (!form.timezone) errors.timezone = 'Timezone is required';
  if (form.visibility_scope === 'agency' && !form.audience_agency_id?.trim()) {
    errors.audience_agency_id = 'Agency is required for agency scope';
  }
  if (form.visibility_scope === 'specific_users' && (form.audience_user_ids || []).length === 0) {
    errors.audience_user_ids = 'At least one user is required';
  }
  if (form.expose_meeting_url_publicly && form.visibility_scope !== 'public') {
    errors.expose_meeting_url_publicly = 'Public meeting URL requires public scope';
  }
  // Phase 26.8 — Owner rules.
  if (form.owner_type === 'agency') {
    if (form.visibility_scope === 'public') {
      errors.owner_type = 'Agency-owned events cannot be Public';
    }
    if (!form.owner_agency_id?.trim()) {
      errors.owner_agency_id = 'Owner agency is required when owner is Agency';
    }
  }
  return errors;
}
